import { useState, useEffect } from 'react';
import { db, supabase } from '../config/supabase';

export const useSupabaseData = (table, filters = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        let result;
        switch (table) {
          case 'users':
            result = await db.getUsers();
            break;
          case 'reports':
            if (filters.agentId) {
              console.log('Fetching reports for agent:', filters.agentId);
              // 🔧 FIX: Simplified query without multiple relationships
              const { data: agentReports, error: agentReportsError } = await supabase
                .from('reports')
                .select(`
                  *,
                  user:users!user_id(username),
                  agent:users!agent_id(username)
                `)
                .eq('agent_id', filters.agentId)
                .order('created_at', { ascending: false });
              
              if (agentReportsError) {
                throw agentReportsError;
              }
              result = { data: agentReports, error: null };
            } else if (filters.userId) {
              // 🔧 FIX: Simplified query for user reports
              const { data: userReports, error: userReportsError } = await supabase
                .from('reports')
                .select(`
                  *,
                  user:users!user_id(username),
                  agent:users!agent_id(username)
                `)
                .eq('user_id', filters.userId)
                .order('created_at', { ascending: false });
              
              if (userReportsError) {
                throw userReportsError;
              }
              result = { data: userReports, error: null };
            } else {
              result = await db.getReports();
            }
            break;
          case 'news':
            if (filters.agentId) {
              result = await db.getNewsByAgent(filters.agentId);
            } else {
              result = await db.getNews();
            }
            break;
          default:
            throw new Error(`Unknown table: ${table}`);
        }
        if (result.error) {
          throw result.error;
        }
        console.log(`Fetched ${table} data:`, result.data);
        setData(result.data || []);
      } catch (err) {
        console.error(`Error fetching ${table}:`, err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Set up real-time subscription
    let subscription;
    if (table === 'reports') {
      subscription = db.subscribeToReports(() => {
        fetchData();
      });
    } else if (table === 'news') {
      subscription = db.subscribeToNews(() => {
        fetchData();
      });
    }

    return () => {
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, [table, JSON.stringify(filters)]);

  const createItem = async (itemData) => {
    try {
      let result;
      switch (table) {
        case 'users':
          console.log('Creating user with data:', itemData);
          result = await db.createUser(itemData);
          break;
        case 'reports':
          console.log('Creating report with data:', itemData);
          // 🔧 FIX: Use direct Supabase insert with explicit relationship syntax
          const { data: newReport, error: reportError } = await supabase
            .from('reports')
            .insert([itemData])
            .select(`
              *,
              user:users!user_id(username),
              agent:users!agent_id(username)
            `)
            .single();
          
          if (reportError) {
            throw reportError;
          }
          
          result = { data: newReport, error: null };
          break;
        case 'news':
          result = await db.createNews(itemData);
          break;
        default:
          throw new Error(`Unknown table: ${table}`);
      }
      
      console.log('Create result:', result);
      
      if (result.error) {
        throw result.error;
      }
      
      // Refresh data after successful creation
      const refreshData = async () => {
        try {
          let refreshResult;
          switch (table) {
            case 'users':
              refreshResult = await db.getUsers();
              break;
            case 'reports':
              if (filters.agentId) {
                // 🔧 FIX: Refresh with explicit relationship syntax
                const { data: agentReports, error: agentReportsError } = await supabase
                  .from('reports')
                  .select(`
                    *,
                    user:users!user_id(username),
                    agent:users!agent_id(username)
                  `)
                  .eq('agent_id', filters.agentId)
                  .order('created_at', { ascending: false });
                
                if (agentReportsError) {
                  throw agentReportsError;
                }
                refreshResult = { data: agentReports, error: null };
              } else if (filters.userId) {
                // 🔧 FIX: Refresh with explicit relationship syntax
                const { data: userReports, error: userReportsError } = await supabase
                  .from('reports')
                  .select(`
                    *,
                    user:users!user_id(username),
                    agent:users!agent_id(username)
                  `)
                  .eq('user_id', filters.userId)
                  .order('created_at', { ascending: false });
                
                if (userReportsError) {
                  throw userReportsError;
                }
                refreshResult = { data: userReports, error: null };
              } else {
                refreshResult = await db.getReports();
              }
              break;
            case 'news':
              if (filters.agentId) {
                refreshResult = await db.getNewsByAgent(filters.agentId);
              } else {
                refreshResult = await db.getNews();
              }
              break;
          }
          if (refreshResult.data) {
            setData(refreshResult.data);
          }
        } catch (refreshErr) {
          console.error('Error refreshing data:', refreshErr);
        }
      };
      
      await refreshData();
      return result.data;
    } catch (err) {
      console.error('Error creating item:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateItem = async (id, updates) => {
    try {
      let result;
      switch (table) {
        case 'users':
          result = await db.updateUser(id, updates);
          break;
        case 'reports':
          result = await db.updateReport(id, updates);
          break;
        case 'news':
          result = await db.updateNews(id, updates);
          break;
        default:
          throw new Error(`Unknown table: ${table}`);
      }
      if (result.error) {
        throw result.error;
      }
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const deleteItem = async (id) => {
    try {
      let error;
      switch (table) {
        case 'users':
          ({ error } = await db.deleteUser(id));
          break;
        case 'reports':
          ({ error } = await db.deleteReport(id));
          break;
        case 'news':
          ({ error } = await db.deleteNews(id));
          break;
        default:
          throw new Error(`Unknown table: ${table}`);
      }
      if (error) {
        throw error;
      }
      // Update local state
      setData(prev => prev.filter(item => item.id !== id));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return {
    data,
    loading,
    error,
    createItem,
    updateItem,
    deleteItem,
    refetch: () => {
      const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
          let result;
          switch (table) {
            case 'users':
              result = await db.getUsers();
              break;
            case 'reports':
              if (filters.agentId) {
                // 🔧 FIX: Refetch with explicit relationship syntax
                const { data: agentReports, error: agentReportsError } = await supabase
                  .from('reports')
                  .select(`
                    *,
                    user:users!user_id(username),
                    agent:users!agent_id(username)
                  `)
                  .eq('agent_id', filters.agentId)
                  .order('created_at', { ascending: false });
                
                if (agentReportsError) {
                  throw agentReportsError;
                }
                result = { data: agentReports, error: null };
              } else if (filters.userId) {
                // 🔧 FIX: Refetch with explicit relationship syntax
                const { data: userReports, error: userReportsError } = await supabase
                  .from('reports')
                  .select(`
                    *,
                    user:users!user_id(username),
                    agent:users!agent_id(username)
                  `)
                  .eq('user_id', filters.userId)
                  .order('created_at', { ascending: false });
                
                if (userReportsError) {
                  throw userReportsError;
                }
                result = { data: userReports, error: null };
              } else {
                result = await db.getReports();
              }
              break;
            case 'news':
              if (filters.agentId) {
                result = await db.getNewsByAgent(filters.agentId);
              } else {
                result = await db.getNews();
              }
              break;
            default:
              throw new Error(`Unknown table: ${table}`);
          }
          if (result.error) {
            throw result.error;
          }
          setData(result.data || []);
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  };
};